import { Injectable } from '@angular/core';
import { environment as ENV } from '../../environments/environment';
import { ApiEndpoints as API } from '../../config/api-endpoints';
import { BehaviorSubject, Observable } from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { UserService } from './user.service';
import { IPolicies, POLICYTYPES } from '../shared/models/policies.interface';
import { emptyIEmail } from '../shared/models/email.interface';
import { UtilityService } from './utility.service';
import { tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class PolicyService {

  questionnaireSubmitted$: BehaviorSubject<any> = new BehaviorSubject<any>(false);

  private options = {
    headers: new HttpHeaders({
      'Content-type': 'application/json'
    })
  }
  constructor(
    private http: HttpClient,
    private userService: UserService,
    private utilityService: UtilityService
  ) { }

  setQuestionnaireSubmitted(value: string) {
    sessionStorage.setItem('questionnaireSubmitted', value);
    this.questionnaireSubmitted$.next(value);
  }

  getQuestionnaireSubmitted() {
    return this.questionnaireSubmitted$;
  }

  downloadPCDocument(id, policyNumber: string): Observable<any> {
    let options = { 'responseType': 'arraybuffer' as 'json', 'observe': 'response' as 'body' }
    let shopifyUID = this.userService.getshopifyUserObjectUID();
    if (shopifyUID !== null) {
      return this.http.get(`${ENV.baseUrl.api}${API.DOWNLOAD_PC_DOCUMENT}/${id}` + "/policyNum/" + policyNumber + "?" + "objectUID=" + shopifyUID, options);
    }
    else {
      return this.http.get(`${ENV.baseUrl.api}${API.DOWNLOAD_PC_DOCUMENT}/${id}` + "/policyNum/" + policyNumber, options);
    }
  }

  isGWPolicy() {
    let policyDetails = JSON.parse(sessionStorage.getItem('policyDetails'));
    if (policyDetails) {
      return !!policyDetails.accountNumber && policyDetails.accountNumber != '';  //Returns false
    }
    else {
      return true;
    }
  }

  hasInsights() {
    let policyDetails = JSON.parse(sessionStorage.getItem('policyDetails'));
    if (policyDetails?.dashboardInfo) {
      return true;
    }
    else {
      return false;
    }
  }

  hasSmartUpload() {
    return JSON.parse(sessionStorage.getItem('policyDetails'))?.smartUploadInd == true;
  }

  getSmartUploadUrl(request) {
    let userDetails = JSON.parse(sessionStorage.getItem('userDetails'));
    request = { ...request, internalUser: userDetails?.internalUser ? true : false };
    return this.http.post<{ url: string }>(`${ENV.baseUrl.api}${API.SMART_UPLOAD}`, request).pipe(
      tap(data => {
        if (data?.url.indexOf('?id=') > 0) {
          sessionStorage.setItem('smartUploadSession', JSON.stringify(
            { ...request, terminateSession: data.url.split('?id=')[1] }
          ));
        }
      })
    );
  }

  hasDeliveryDefense() {
    if (JSON.parse(sessionStorage.getItem('policyDetails'))?.ref1 == 'DD' && JSON.parse(sessionStorage.getItem('policyDetails'))?.ref2) {
      return true;
    } else {
      return false;
    }
  }

  hasShipVision() {
    if (JSON.parse(sessionStorage.getItem('policyDetails'))?.shipVision) {
      return true;
    } else {
      return false;
    }
  }

  isConnectPolicy() {
    if (JSON.parse(sessionStorage.getItem('policyDetails'))?.hasTCCoverage === true || JSON.parse(sessionStorage.getItem('policyDetails'))?.subPolicyType?.toLowerCase() === 'connect' ||
      this.determinePolicyType(JSON.parse(sessionStorage.getItem('policyDetails'))) == POLICYTYPES.TRANSACTIONAL_CARGO) {
      return true;
    } else {
      return false;
    }
  }

  isChannelPartnerPolicy() {
    if (JSON.parse(sessionStorage.getItem('policyDetails'))?.isChannelPartner == true) {
      return true;
    } else {
      return false;
    }
  }

  isTUPSSContractNumber() {
    if (JSON.parse(sessionStorage.getItem('policyDetails'))?.policyNumber?.startsWith('623876-')) {
      return true;
    } else {
      return false;
    }
  }

  isThreeHyphenContractNo() {
    if (JSON.parse(sessionStorage.getItem('policyDetails'))?.policyNumber?.startsWith('3-')) {
      return true;
    } else {
      return false;
    }
  }

  isTwoHyphenContractNo() {
    if (JSON.parse(sessionStorage.getItem('policyDetails'))?.policyNumber?.startsWith('2-')) {
      return true;
    } else {
      return false;
    }
  }

  isCLMS() {
    let policyDetails = JSON.parse(sessionStorage.getItem('policyDetails'));
    if (policyDetails !== undefined && policyDetails !== null) {
      if (policyDetails.ref5 === 'CLMS' && policyDetails.ref6 !== null) {
        return true;
      } else {
        return false;
      }
    } else {
      return false;
    }
  }

  isPirateShipFlow() {
    return sessionStorage.getItem('flowType') === 'pirateship' ? true : false;
  }

  getDDAPIUrl(request) {
    return this.http.post<any>(`${ENV.baseUrl.api}${API.DELIVERY_DEFENSE_2_1}`, request);
  }

  getDeliveryDefenseUrl(request) {
    return this.http.post<any>(`${ENV.baseUrl.api}${API.DELIVERY_DEFENSE}`, request);
  }

  generateDDContractNumber(request) {
    return this.http.post<any>(`${ENV.baseUrl.api}${API.DD_CONTRACT_NUMBER}`, request);
  }

  hasPolicyDetails() {
    let policyDetails = JSON.parse(sessionStorage.getItem('policyDetails'));
    if (policyDetails?.policyNumber) {
      return true;
    }
    else {
      return false;
    }
  }

  checkCoverage(tn: string) {
    return this.http.post<any>(`${ENV.baseUrl.api}${API.CHECK_COVERAGE}`, { trackingNumber: tn });
  }

  createPolicy(request) {
    return this.http.post<any>(`${ENV.baseUrl.api}${API.CREATE_POLICY}`, request);
  }

  submitPolicy(request) {
    return this.http.post<any>(`${ENV.baseUrl.api}${API.SUBMIT_POLICY}`, request);
  }

  addNewPolicy(request) {
    return this.http.post<any>(`${ENV.baseUrl.api}${API.ADD_POLICIES}`, request);
  }

  updatePolicyNickname(request) {
    return this.http.post<any>(`${ENV.baseUrl.api}${API.UPDATE_NICKNAME}`, request);
  }

  getUserPolicyDetails(request) {
    return this.http.post<any>(`${ENV.baseUrl.api}${API.POLICY_DETAILS}`, request);
  }

  getChildPolicyInfo(request) {
    return this.http.post<any>(`${ENV.baseUrl.api}${API.CHILD_POLICY_INFO}`, request);
  }

  getPolicyShipperAccounts(request) {
    return this.http.post<any>(`${ENV.baseUrl.api}${API.SHIPPER_ACCOUNTS}`, request);
  }

  isTpTcPolicy(policyInfo: any) {
    let isTpTcPolicy = false;
    var result = this.determinePolicyType(policyInfo);
    if (result) {
      if (result === POLICYTYPES.TRANSACTIONAL_CARGO || result === POLICYTYPES.TRANSACTIONAL_PARCEL) {
        isTpTcPolicy = true;
      }
      else {
        isTpTcPolicy = false;
      }
    }
    return isTpTcPolicy;
  }

  isToggleExpandedFor(appRoleId: string) {
    let policyDetails = JSON.parse(sessionStorage.getItem('policyDetails'));

    let toggle = policyDetails?.policyCustomization?.find(item => {
      if (item?.feature == 'TOGGLE-POLICY' && item.action == 'EXPANDED') {
        if (item.appRoleIds?.split(',').indexOf(appRoleId) > -1) {
          return true;
        }
      }
    });
    return !!toggle;
  }

  isCREExcluded() {
    let policyDetails = JSON.parse(sessionStorage.getItem('policyDetails'));
    if (['dd', 'dda'].indexOf(policyDetails?.policySource?.toLowerCase()) > -1) {
      return true;
    }

    let cre = policyDetails?.policyCustomization?.find(item => item?.feature == 'CRE' && item.action == 'EXCLUDED');
    return !!cre;
  }

  //Transactional Parcel = Flexible Parcle = FLEX = InsureShield for UPS Packages
  //Traditional Cargo = Cargo = InsureShield Complete (coverage type = traditional, ref1 should be null)
  //Transactional Cargo = TC = InsureShield Connect (coverage type = transactional, ref1 should have "api" / "smart upload" / "webhooks" / "other") - CAN ALSO JUST BE "hasTCCoverage = true"
  determinePolicyType(policy: IPolicies): POLICYTYPES {
    //Need these fields to determine a policy type

    if (policy && policy.policyType && policy.coverageType && policy.subPolicyType) {
      let policytype = policy.policyType.key?.toLowerCase();
      let coverage = policy.coverageType?.toLowerCase();
      let subtype = policy.subPolicyType?.toLowerCase();
      let hasTCOverride = policy.hasTCCoverage;

      //TC Override has to be the first if statement - if this is true, it does not matter at all what the rest of the indicators are.
      if (hasTCOverride) {
        return POLICYTYPES.TRANSACTIONAL_CARGO;
      }

      if ((subtype == 'for ups packages' || subtype == 'forupspackage') || (policytype == 'flex')) {
        return POLICYTYPES.TRANSACTIONAL_PARCEL;
      }
      if (coverage == 'traditional' && subtype == 'complete') {
        return POLICYTYPES.TRADITIONAL_CARGO;
      }

      //SMB is currently coming into TC - we need an additional field to be able to tell if SMB
      if (coverage == 'transactional' && subtype == 'connect') {
        return POLICYTYPES.TRANSACTIONAL_CARGO;
      }

      //If it did not match any of the above
      return POLICYTYPES.UNKNOWN;
    } else {
      return POLICYTYPES.UNKNOWN;
    }
  }

  getListOfShipperNumbers(request) {
    return this.http.post<any>(`${ENV.baseUrl.api}${API.SHIPPER_AND_GROUP_LIST}`, request);
  }

  getListOfChildPolicies(request) {
    return this.http.post<any>(`${ENV.baseUrl.api}${API.CHILD_POLICY_LIST}`, request);
  }

  public checkPolicyExist(request: any): Observable<any> {
    return this.http.post<any>(`${ENV.baseUrl.api}${API.GET_SSP_INFO}`, request, this.options)
  }

  public getPolicyGroupCodeDetails(request: any): Observable<any> {
    return this.http.post<any>(`${ENV.baseUrl.api}${API.GET_POLICY_GROUPCODE}`, request, this.options)
  }


  getIS4UPSQuoteDetails() {
    let conversionInfo = JSON.parse(sessionStorage.getItem('is4upsQuoteDetails'));
    return conversionInfo;
  }

  // update values in IS4UPSAcquisitions table
  updateIS4UPSInfo(request) {
    return this.http.post<any>(`${ENV.baseUrl.api}${API.UPSERT_ACQUISITION}`, request);
  }

  // retrieve values from IS4UPSAcquisitions table
  retrieveIS4UPSInfo(policyNumber) {
    return this.http.get<any>(`${ENV.baseUrl.api}${API.GET_ACQUISITION}/${policyNumber}`, this.options);
  }

  getShipperNumbers(request) {
    return this.http.post<any>(`${ENV.baseUrl.api}${API.GET_SHIPPER_NUMBERS}`, request);
  }

  updateIS4UPSUserProfile(request){
    return this.http.post<any>(`${ENV.baseUrl.api}${API.LEAD_PROFILE_CREATE}`,request);
  }

  // validateShipperNumber(request) {//Unused API
  //   return this.http.post<any>(`${ENV.baseUrl.api}${API.VALIDATE_SHIPPER_NUMBER}`, request);
  // }

  validateMultipleShipperNumbers(request) {
    return this.http.post<any>(`${ENV.baseUrl.api}${API.VALIDATE_MULTIPLE_SHIPPER_NUMBER}`, request);
  }

  sendIS4UPSConversionEmail(request) {
    return this.http.post<any>(`${ENV.baseUrl.api}${API.CONVERSION_EMAIL}`, request);
  }

  getRenewalQuestionnaireInfo(request) {
    return this.http.post<any>(`${ENV.baseUrl.api}${API.GET_RENEWAL_QUESTIONNAIRE_INFO}`, request);
  }

  submitRenewalQuestionnaire(request) {
    return this.http.post<any>(`${ENV.baseUrl.api}${API.SUBMIT_RENEWAL_QUESTIONNAIRE}`, request);
  }


  getPolicNumbersWithRef2Ref6(policyNumber: string) {

    let _policyNumbers = policyNumber;

    let _ref6Policy = JSON.parse(sessionStorage.getItem('policyDetails')).ref6;
    if (_ref6Policy !== null && _ref6Policy.length > 0) {
      _policyNumbers = _policyNumbers + ',' + _ref6Policy;
    }

    let _ref2Policy = JSON.parse(sessionStorage.getItem('policyDetails')).ref2;
    if (_ref2Policy !== null && _ref2Policy.length > 0) {
      _policyNumbers = _policyNumbers + ',' + _ref2Policy;
    }
    return _policyNumbers;
  }


   getPolicNumbersWithef6(policyNumber: string) {

    let _policyNumbers = policyNumber;

    let _ref6Policy = JSON.parse(sessionStorage.getItem('policyDetails')).ref6;
    if (_ref6Policy !== null && _ref6Policy.length > 0) {
      _policyNumbers = _policyNumbers + ',' + _ref6Policy;
    }

    return _policyNumbers;
  }
}
